import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";

import { analyticsApi } from "@/api/analytics";
import { logMsg, logEx, crumb } from "@/config/sentry-logger";

import type { AxiosError } from "axios";
import type { EAnalyticsEvent } from "@/modules/analytics/consts/analytics-event";
import type { RootState } from "@/store";

export const useShareAnalyticsEvent = () => {
  const initData = useSelector((state: RootState) => state.telegram.initData);

  const { mutateAsync: shareAnalyticsEvent, ...queryParams } = useMutation<
    unknown,
    unknown,
    EAnalyticsEvent
  >({
    mutationKey: ["analytics-event", initData],
    mutationFn: async (eventType) => {
      if (!initData) {
        logMsg("shareAnalyticsEvent rejected: no-init-data", "warning", {
          hasInitData: false,
          eventType,
        });
        throw new Error("no-init-data");
      }

      crumb("analytics: start", { eventType });

      return analyticsApi.shareAnalyticsEvent(initData, eventType);
    },
    onSuccess: (_data, eventType) => {
      crumb("analytics: success", { eventType });
      logMsg("shareAnalyticsEvent success", "info", { eventType });
    },
    onError: (error, eventType) => {
      const ax = error as AxiosError;
      const status = ax?.response?.status;
      const extra = {
        status,
        code: (ax as any)?.code,
        method: ax?.config?.method,
        url: ax?.config?.url,
        hasInitData: !!initData,
        eventType,
      };
      const level = !status ? "error" : status >= 500 ? "error" : "warning";
      logMsg("shareAnalyticsEvent error", level, extra);
      logEx(error, "shareAnalyticsEvent mutation error", extra);
    },
  });

  return {
    shareAnalyticsEvent,
    ...queryParams,
  };
};
